import React, { useState } from 'react';
import { Star, Send, Loader2, CheckCircle, Target, Clock, Trophy, XCircle } from 'lucide-react';
import { Button } from './button';

const API = "/server/JurassicServer";

// Game Session Summary Component
export function GameSessionSummary({
  dinosaursCaptured = 0,
  totalDinosaurs = 7,
  capturedList = [],
  feedbackType = 'TIME_UP'
}) {
  const isVictory = feedbackType === 'VICTORY';
  const isFailed = feedbackType === 'FAILED';
  const isTimeUp = feedbackType === 'TIME_UP';

  const getStyles = () => {
    if (isVictory) return { bg: 'bg-green-900/20 border-green-600/30', text: 'text-green-400' };
    if (isFailed) return { bg: 'bg-red-900/20 border-red-600/30', text: 'text-red-400' };
    return { bg: 'bg-orange-900/20 border-orange-600/30', text: 'text-orange-400' };
  };

  const styles = getStyles();

  const getTitle = () => {
    if (isVictory) return 'Mission Complete!';
    if (isFailed) return 'Mission Failed';
    return 'Time Up';
  };

  const getIcon = () => {
    if (isVictory) return <Trophy className="w-5 h-5 text-green-400" />;
    if (isFailed) return <XCircle className="w-5 h-5 text-red-400" />;
    return <Clock className="w-5 h-5 text-orange-400" />;
  };

  return (
    <div className={`rounded-lg p-4 mb-6 border ${styles.bg}`}>
      <div className="flex items-center gap-2 mb-3">
        {getIcon()}
        <h3 className={`font-bold ${styles.text}`}>
          {getTitle()}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-black/30 rounded-lg p-3 text-center">
          <p className="text-gray-400 text-xs mb-1">Dinosaurs Captured</p>
          <p className={`text-2xl font-bold ${styles.text}`}>
            {dinosaursCaptured} / {totalDinosaurs}
          </p>
        </div>
        <div className="bg-black/30 rounded-lg p-3 text-center">
          <p className="text-gray-400 text-xs mb-1">Success Rate</p>
          <p className={`text-2xl font-bold ${styles.text}`}>
            {Math.round((dinosaursCaptured / totalDinosaurs) * 100)}%
          </p>
        </div>
      </div>

      {capturedList.length > 0 && (
        <div className="mt-3">
          <p className="text-gray-400 text-xs mb-2">Captured:</p>
          <div className="flex flex-wrap gap-2">
            {capturedList.map((dino, idx) => (
              <span key={idx} className="px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded-full flex items-center gap-1">
                <Target className="w-3 h-3" />
                {dino}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function FeedbackForm({
  feedbackType = 'TIME_UP', // 'VICTORY' or 'TIME_UP'
  dinosaursCaptured = 0,
  totalDinosaurs = 7,
  capturedList = [],
  initialValues = null, // { rating, gameExperience, delugeLearning, suggestions }
  onSubmitSuccess,
  onSkip,
  showSkip = false,
  showSummary = true,
}) {
  const [rating, setRating] = useState(initialValues?.rating || 0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [gameExperience, setGameExperience] = useState(initialValues?.gameExperience || '');
  const [delugeLearning, setDelugeLearning] = useState(initialValues?.delugeLearning || '');
  const [suggestions, setSuggestions] = useState(initialValues?.suggestions || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const isEditing = initialValues !== null;

  const validateFields = () => {
    const errors = {};
    if (rating === 0) errors.rating = 'Please select a rating';
    if (!gameExperience.trim()) errors.gameExperience = 'This field is required';
    if (!delugeLearning.trim()) errors.delugeLearning = 'This field is required';
    if (!suggestions.trim()) errors.suggestions = 'This field is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API}/submitFeedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedbackType,
          rating,
          gameExperience,
          delugeLearning,
          suggestions,
          dinosaursCaptured,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit feedback');
      }

      setSubmitted(true);
      if (onSubmitSuccess) {
        onSubmitSuccess({
          rating,
          gameExperience,
          delugeLearning,
          suggestions,
          dinosaursCaptured,
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-green-400 mb-2">Thank You!</h3>
        <p className="text-gray-300">Your feedback has been submitted successfully.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Game Session Summary */}
      {showSummary && (
        <GameSessionSummary
          dinosaursCaptured={dinosaursCaptured}
          totalDinosaurs={totalDinosaurs}
          capturedList={capturedList}
          feedbackType={feedbackType}
        />
      )}

      {/* Rating */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          How would you rate your overall experience? <span className="text-red-400">*</span>
        </label>
        <div className="flex gap-2 justify-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => { setRating(star); setFieldErrors(prev => ({...prev, rating: null})); }}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`w-8 h-8 ${
                  star <= (hoveredRating || rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-500'
                }`}
              />
            </button>
          ))}
        </div>
        <p className="text-center text-sm text-gray-400 mt-1">
          {rating === 1 && 'Poor'}
          {rating === 2 && 'Fair'}
          {rating === 3 && 'Good'}
          {rating === 4 && 'Very Good'}
          {rating === 5 && 'Excellent'}
        </p>
        {fieldErrors.rating && (
          <p className="text-center text-sm text-red-400 mt-1">{fieldErrors.rating}</p>
        )}
      </div>

      {/* Game Experience */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          How was your game experience? <span className="text-red-400">*</span>
        </label>
        <textarea
          value={gameExperience}
          onChange={(e) => { setGameExperience(e.target.value); setFieldErrors(prev => ({...prev, gameExperience: null})); }}
          placeholder="Share your thoughts about the gameplay, challenges, UI..."
          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none ${
            fieldErrors.gameExperience ? 'border-red-500' : 'border-gray-600'
          }`}
          rows={3}
        />
        {fieldErrors.gameExperience && (
          <p className="text-sm text-red-400 mt-1">{fieldErrors.gameExperience}</p>
        )}
      </div>

      {/* Deluge Learning */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Did this help you learn Deluge? <span className="text-red-400">*</span>
        </label>
        <textarea
          value={delugeLearning}
          onChange={(e) => { setDelugeLearning(e.target.value); setFieldErrors(prev => ({...prev, delugeLearning: null})); }}
          placeholder="Tell us about your Deluge learning experience..."
          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none ${
            fieldErrors.delugeLearning ? 'border-red-500' : 'border-gray-600'
          }`}
          rows={3}
        />
        {fieldErrors.delugeLearning && (
          <p className="text-sm text-red-400 mt-1">{fieldErrors.delugeLearning}</p>
        )}
      </div>

      {/* Suggestions */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Any suggestions for improvement? <span className="text-red-400">*</span>
        </label>
        <textarea
          value={suggestions}
          onChange={(e) => { setSuggestions(e.target.value); setFieldErrors(prev => ({...prev, suggestions: null})); }}
          placeholder="What could we do better? Any features you'd like to see?"
          className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none ${
            fieldErrors.suggestions ? 'border-red-500' : 'border-gray-600'
          }`}
          rows={3}
        />
        {fieldErrors.suggestions && (
          <p className="text-sm text-red-400 mt-1">{fieldErrors.suggestions}</p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-center">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {showSkip && onSkip && (
          <Button
            type="button"
            onClick={onSkip}
            variant="outline"
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600"
          >
            Skip
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {isEditing ? 'Updating...' : 'Submitting...'}
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              {isEditing ? 'Update Feedback' : 'Submit Feedback'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export default FeedbackForm;
